import {subscribeOffline,offlineState} from './offlineVault';
import { useQueryClient } from '@tanstack/react-query';
// Auth context. Holds the current clinician + drives the login redirect.
// On mount we read the token from storage; if present we probe /clinician/v1/me
// to validate the session. 401 → clear + treat as logged out.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Clinician } from '@pamosi/shared';
import { authApi, ApiError } from './api';
import {
    getAccessToken, setAccessToken, setRefreshToken, clearSession,
} from './storage';

interface AuthState {
    status: 'loading' | 'authed' | 'anon';
    clinician: Clinician | null;
}

interface AuthContextValue extends AuthState {
    signIn(args: { accessToken: string; refreshToken: string; clinician: Clinician }): void;
    signOut(): void;
    refresh(): Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const [state, setState] = useState<AuthState>({ status: 'loading', clinician: null });

    async function probe() {
        const token = getAccessToken();
        if (!token) {
            setState({ status: 'anon', clinician: null });
            return;
        }
        try {
            const res = await authApi.me();
            setState({ status: 'authed', clinician: res.clinician });
        } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                queryClient.clear();
                clearSession();
                setState({ status: 'anon', clinician: null });
            } else {
                // Network glitch etc. — keep the token, mark as anon for now
                // so the app renders. User can retry login manually.
                setState({ status: 'anon', clinician: null });
            }
        }
    }

    useEffect(() => { void probe();let unlocked=!!offlineState();return subscribeOffline(()=>{const now=!!offlineState();if(now&&!unlocked&&getAccessToken())void probe();unlocked=now;}); }, []);

    const value: AuthContextValue = {
        ...state,
        signIn({ accessToken, refreshToken, clinician }) {
            window.dispatchEvent(new Event('pamosi-offline-lock'));
            queryClient.clear();
            setAccessToken(accessToken);
            setRefreshToken(refreshToken);
            setState({ status: 'authed', clinician });
        },
        signOut() {
            window.dispatchEvent(new Event('pamosi-offline-lock'));
            queryClient.clear();
            clearSession();
            setState({ status: 'anon', clinician: null });
        },
        refresh: probe,
    };
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
    const v = useContext(Ctx);
    if (!v) throw new Error('useAuth must be used inside AuthProvider');
    return v;
}
